import React, { MouseEvent, useState } from 'react';
import FindReplaceIcon from '@mui/icons-material/FindReplace';
import SettingsIcon from '@mui/icons-material/Settings';
import {
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Paper,
  Popover,
  Tooltip,
} from '@mui/material';

import {
  hasCanUsePlatesPermission,
  hasOptimizationFeatureFlag as hasOptimizationFeatureFlagSelector,
  hasTrussDesignPermission as hasTrussDesignPermissionSelector,
} from '@paragon/login-tools';

import { useAppMonitor } from '../../../monitor';
import { upgradeAndAnalyzeActiveComponentDesign } from '../../../store/actions/paragonProcessActions';
import { getActiveComponentDesign } from '../../../store/selectors/getActiveComponentDesignSelector';
import { isParagonJobOpen, isParagonRepairJobOpen } from '../../../store/selectors/jobContextSelectors';
import { isLayoutViewActive } from '../../../store/selectors/layoutModeSelectors';
import {
  inViewOnlyMode as componentIsViewOnlySelector,
  isViewingOldAnalysisSet,
} from '../../../store/selectors/viewOnlyModeSelectors';
import {
  setAllowOptimizationChordResizing,
  setDetailedAnalysis,
  setRegenerateTddsAutomatically,
  setRunExhaustivePlateSearch,
  setRunOptimizations,
  setRunPlateMislocations,
} from '../../../store/slices/applicationSlice';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { ToolbarIconButton } from '../top/ToolbarIconButton';

const ANALYZE_BUTTON_ID = 'analyze-button';

interface Props {
  condensed?: boolean;
}

export default function AnalyzeButton({ condensed }: Props) {
  const dispatch = useAppDispatch();
  const monitor = useAppMonitor();

  const [isPopoverOpen, setPopoverOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const inLayoutMode = useAppSelector(isLayoutViewActive);
  const hasTrussDesignPermission = useAppSelector(hasTrussDesignPermissionSelector);
  const componentIsViewOnly = useAppSelector(componentIsViewOnlySelector);
  const hasOptimizationFeatureFlag = useAppSelector(hasOptimizationFeatureFlagSelector);
  const canUsePlates = useAppSelector(hasCanUsePlatesPermission);

  const detailedAnalysis = useAppSelector((state) => state.application.detailedAnalysis);
  const regenerateTddsAutomatically = useAppSelector((state) => state.application.regenerateTddsAutomatically);
  const runPlateMislocations = useAppSelector((state) => state.application.runPlateMislocations);
  const runExhaustivePlateSearch = useAppSelector((state) => state.application.runExhaustivePlateSearch);
  const runOptimizations = useAppSelector((state) => state.application.runOptimizations);
  const allowOptimizationChordResizing = useAppSelector((state) => state.application.allowOptimizationChordResizing);
  const isParagonJob = useAppSelector(isParagonJobOpen);
  const isParagonRepairJob = useAppSelector(isParagonRepairJobOpen);
  const viewingOldAnalysisSet = useAppSelector(isViewingOldAnalysisSet);

  const activeComponentDesign = useAppSelector(getActiveComponentDesign);

  if (inLayoutMode || !hasTrussDesignPermission) return null;

  function onPopoverClose() {
    setPopoverOpen(false);
  }

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setPopoverOpen(true);
    setAnchorEl(event.currentTarget);
  };

  const coreAnalyzeButtonProps = {
    id: ANALYZE_BUTTON_ID,
    onClick: () => {
      monitor.recordInteraction('Analyze button clicked');
      dispatch(upgradeAndAnalyzeActiveComponentDesign());
    },
    disabled: activeComponentDesign == null || componentIsViewOnly,
  } as const;

  if (condensed) {
    return <ToolbarIconButton title="Analyze" icon={<FindReplaceIcon />} {...coreAnalyzeButtonProps} />;
  }

  return (
    <>
      {isPopoverOpen && (
        <Popover
          open={isPopoverOpen}
          anchorEl={anchorEl}
          onClose={(event, reason) => {
            onPopoverClose();
            setAnchorEl(null);
            // If the backdrop was clicked, we want to re-dispatch that event so that it'll also occur on the underlying
            //   element below the backdrop.  This will allow the user to successfully click the "Analyze" button while
            //   the popover is still open.
            // https://trello.com/c/8aJGloOU/6777-2-combine-analysis-buttons-into-one-button-with-options#comment-5e7cf7144d0fb25e672efc05
            if (reason === 'backdropClick') {
              const { pageX, pageY } = (event as MouseEvent).nativeEvent;
              // When this event handler is called, the popover is still open, so immediately calling `elementFromPoint`
              //   will only return the backdrop.  Thus, we want to use `setTimeout` to schedule it to be run after all
              //   pending tasks have completed and the UI update has been applied.
              setTimeout(() => {
                let item = document.elementFromPoint(pageX, pageY);
                while (item) {
                  if (item.id === ANALYZE_BUTTON_ID) {
                    const analyzeButton = item as HTMLElement;
                    analyzeButton.click();
                    return;
                  }
                  item = item.parentElement;
                }
              }, 0);
            }
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <Paper elevation={1} sx={{ p: 1 }}>
            <Box sx={{ mb: 1 }}>
              <FormControl component="fieldset">
                <FormLabel component="legend">Analysis Options</FormLabel>
                <FormGroup>
                  <FormControlLabel
                    label="Detailed"
                    control={
                      <Checkbox
                        checked={detailedAnalysis}
                        onChange={(event) => void dispatch(setDetailedAnalysis(event.target.checked))}
                      />
                    }
                  />
                  {isParagonRepairJob && canUsePlates && (
                    <FormControlLabel
                      label="Analyze plate mislocations"
                      control={
                        <Checkbox
                          checked={runPlateMislocations}
                          onChange={(event) => void dispatch(setRunPlateMislocations(event.target.checked))}
                        />
                      }
                    />
                  )}
                  {hasOptimizationFeatureFlag && canUsePlates && (
                    <FormControlLabel
                      label="Exhaustive plate search"
                      control={
                        <Checkbox
                          checked={runExhaustivePlateSearch}
                          onChange={(event) => void dispatch(setRunExhaustivePlateSearch(event.target.checked))}
                        />
                      }
                    />
                  )}
                  {hasOptimizationFeatureFlag && (
                    <FormControlLabel
                      label="Run optimizations"
                      control={
                        <Checkbox
                          checked={runOptimizations}
                          onChange={(event) => void dispatch(setRunOptimizations(event.target.checked))}
                        />
                      }
                    />
                  )}
                  {hasOptimizationFeatureFlag && (
                    <FormControlLabel
                      label="Allow resizing chords"
                      control={
                        <Checkbox
                          checked={allowOptimizationChordResizing}
                          onChange={(event) => void dispatch(setAllowOptimizationChordResizing(event.target.checked))}
                        />
                      }
                    />
                  )}
                  {isParagonJob && (
                    <FormControlLabel
                      label="Regenerate TDDs automatically"
                      control={
                        <Checkbox
                          checked={regenerateTddsAutomatically}
                          onChange={(event) => void dispatch(setRegenerateTddsAutomatically(event.target.checked))}
                        />
                      }
                    />
                  )}
                </FormGroup>
              </FormControl>
            </Box>
          </Paper>
        </Popover>
      )}

      <ButtonGroup>
        <Tooltip
          placement="bottom"
          title={viewingOldAnalysisSet ? 'The component is in view-only mode.' : 'Analyze (Ctrl + A)'}
        >
          <span>
            <Button
              size="small"
              variant="contained"
              color="secondary"
              aria-label="analyze"
              startIcon={<FindReplaceIcon />}
              {...coreAnalyzeButtonProps}
            >
              Analyze
            </Button>
          </span>
        </Tooltip>

        <Tooltip disableInteractive title="Analysis Options">
          <span>
            <Button
              size="small"
              variant="contained"
              color="secondary"
              onClick={handleClick}
              disabled={activeComponentDesign == null || componentIsViewOnly}
              sx={{ '> :first-child': { marginRight: '-4px' } }}
              startIcon={<SettingsIcon />}
            >
              <span style={{ width: '0' }}>&nbsp;</span>
            </Button>
          </span>
        </Tooltip>
      </ButtonGroup>
    </>
  );
}
